import { Box, Chip, Divider, Typography } from "@mui/material";
import type { IHighlight } from "@argument-studio/react-pdf-highlighter-with-categories";

// Detailed rubric breakdown for the resource currently open in the pane,
// shown (expandable) on its gradebook card. Recovered from the retired
// GradeSection reader/canvas cards; the grading itself now lives on the
// card, so this is read-only diagnostics. Visual labels use div/span — no
// misused heading elements.

interface Dim {
  enabled: boolean;
  score: number | null;
  violations: number;
}

interface ReaderData {
  hasExpertReference: boolean;
  completeness: Dim;
  categoryAccuracy: Dim;
  coverageQuality: number;
  matchedCount: number;
  partialCount: number;
  fullyMissedCount: number;
  expertCount: number;
  missedHighlights: IHighlight[];
}

interface CanvasData {
  hasExpertReference: boolean;
  nodeCoverage: Dim;
  categoryAccuracy: Dim;
  edgeCoverage: Dim;
  matchedNodeCount: number;
  expertNodeCount: number;
  matchedEdgeCount: number;
  expertEdgeCount: number;
  duplicateStudentCount: number;
  orphanStudentCount: number;
  extraEdgeCount: number;
  parsons: { expertViolationCount: number; studentViolationCount: number };
}

const DimensionRow = ({
  label,
  dim,
  violationLabel,
}: {
  label: string;
  dim: Dim;
  violationLabel: string;
}) => {
  if (!dim.enabled) {
    return (
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="body2" color="text.disabled">
          {label}
        </Typography>
        <Chip label="disabled" size="small" variant="outlined" />
      </Box>
    );
  }
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <Typography variant="body2">{label}</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {dim.violations} {violationLabel}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: "bold", minWidth: 56, textAlign: "right" }}>
          {dim.score ?? "—"}/100
        </Typography>
      </Box>
    </Box>
  );
};

const LegendRow = ({
  swatchStyle,
  label,
}: {
  swatchStyle: React.CSSProperties;
  label: string;
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Box
      sx={{
        width: 24,
        height: 12,
        backgroundColor: "rgba(0,0,0,0.06)",
        flexShrink: 0,
        ...swatchStyle,
      }}
    />
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

const previewHighlight = (h: IHighlight): string => {
  if (h.content?.text) {
    const trimmed = h.content.text.slice(0, 60);
    return trimmed.length < h.content.text.length ? `${trimmed}…` : trimmed;
  }
  if (h.content?.image) return "(image)";
  return "";
};

const noExpertMsg = (kind: "reading" | "canvas") => (
  <Typography variant="body2" color="text.secondary">
    No expert reference saved for this {kind}. Open the Rubric tab and save
    the current {kind === "reading" ? "highlights" : "canvas"} as expert to
    enable auto-grading.
  </Typography>
);

function ReaderBreakdown({ data }: { data: ReaderData }) {
  if (!data.hasExpertReference) return noExpertMsg("reading");
  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1.5 }}>
        <DimensionRow label="Completeness" dim={data.completeness} violationLabel="missed" />
        <DimensionRow label="Category accuracy" dim={data.categoryAccuracy} violationLabel="mismatched" />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
        <Typography variant="caption" color="text.secondary">
          Coverage:
        </Typography>
        <Chip label={`${Math.round(data.coverageQuality * 100)}%`} size="small" variant="outlined" />
        <Typography variant="caption" color="text.secondary">
          {data.matchedCount} fully matched
          {data.partialCount > 0 ? `, ${data.partialCount} partial` : ""}
          {data.fullyMissedCount > 0 ? `, ${data.fullyMissedCount} missed` : ""} /{" "}
          {data.expertCount} expert
        </Typography>
      </Box>
      {data.missedHighlights.length > 0 && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" component="div" sx={{ mb: 0.5 }}>
            Missed ({data.missedHighlights.length})
          </Typography>
          <Box
            component="ul"
            sx={{
              m: 0,
              pl: 2,
              maxHeight: 120,
              overflowY: "auto",
              "& li": { fontSize: "0.85rem", mb: 0.25 },
            }}
          >
            {data.missedHighlights.slice(0, 20).map((h) => (
              <li key={h.id}>
                <Typography variant="caption" color="text.secondary" component="span">
                  p.{h.position.pageNumber}
                </Typography>{" "}
                <Typography variant="body2" component="span">
                  [{h.comment.category}] {previewHighlight(h)}
                </Typography>
              </li>
            ))}
            {data.missedHighlights.length > 20 && (
              <li>
                <Typography variant="caption" color="text.secondary">
                  …and {data.missedHighlights.length - 20} more
                </Typography>
              </li>
            )}
          </Box>
        </>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          mt: 1.5,
          pt: 1,
          borderTop: `1px dashed`,
          borderColor: "divider",
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: "bold" }}>
          PDF reader markup
        </Typography>
        <LegendRow
          swatchStyle={{
            outline: "2px dashed var(--assessment-ghost-stroke)",
            outlineOffset: "-2px",
            opacity: 0.5,
          }}
          label="Expert highlight missed (ghost)"
        />
        <Typography variant="caption" color="text.secondary">
          Other issues (mismatched category, low overlap, extras) show a small
          ⓘ next to the annotation — hover for details.
        </Typography>
      </Box>
    </>
  );
}

function CanvasBreakdown({ data }: { data: CanvasData }) {
  if (!data.hasExpertReference) return noExpertMsg("canvas");
  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1.5 }}>
        <DimensionRow label="Node coverage" dim={data.nodeCoverage} violationLabel="missing" />
        <DimensionRow label="Category accuracy" dim={data.categoryAccuracy} violationLabel="mismatched" />
        <DimensionRow label="Edge coverage" dim={data.edgeCoverage} violationLabel="missing" />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        {data.matchedNodeCount} / {data.expertNodeCount} nodes,{" "}
        {data.matchedEdgeCount} / {data.expertEdgeCount} edges
      </Typography>
      {(data.duplicateStudentCount > 0 ||
        data.orphanStudentCount > 0 ||
        data.extraEdgeCount > 0) && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 0.5, fontStyle: "italic" }}
        >
          Informational:{" "}
          {data.duplicateStudentCount > 0 &&
            `${data.duplicateStudentCount} duplicate-source rect(s); `}
          {data.orphanStudentCount > 0 &&
            `${data.orphanStudentCount} rect(s) without provenance; `}
          {data.extraEdgeCount > 0 && `${data.extraEdgeCount} extra edge(s)`}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        Parsons-condition parity: expert {data.parsons.expertViolationCount}, student{" "}
        {data.parsons.studentViolationCount} violation(s)
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          mt: 1.5,
          pt: 1,
          borderTop: `1px dashed`,
          borderColor: "divider",
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: "bold" }}>
          Canvas markup
        </Typography>
        <LegendRow
          swatchStyle={{
            outline: "2px dashed var(--assessment-ghost-stroke)",
            outlineOffset: "-2px",
            opacity: 0.5,
          }}
          label="Expert rect missed (ghost)"
        />
      </Box>
    </>
  );
}

export default function RubricBreakdown({
  kind,
  reader,
  canvas,
}: {
  kind: "reading" | "canvas";
  reader: ReaderData | null;
  canvas: CanvasData | null;
}) {
  if (kind === "reading" && reader) return <ReaderBreakdown data={reader} />;
  if (kind === "canvas" && canvas) return <CanvasBreakdown data={canvas} />;
  return null;
}
